/**
 * Every plugin's execution server in the running agent, one row each.
 *
 * The Servers tab of the plugins page. A draining row is a swap in progress
 * after an install; a failed row keeps its tasks pending and names why.
 */
import { useQuery } from '@tanstack/react-query';

import { listPluginServers } from '@optum/api-client/endpoints/plugins';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { relativeTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { DataTable } from '../../ui/DataTable.js';
import { Badge, Typography } from '../../ui/finixt.js';
import { megabytes, SERVER_STATE_COLOR } from './ExecutionServerCard.js';

const POLL_MS = 5000;

export function ServersTable() {
  const query = useQuery({
    queryKey: qk.pluginServers(),
    queryFn: listPluginServers,
    refetchInterval: POLL_MS,
  });

  if (query.isPending) return <LoadingBlock />;
  if (query.isError)
    return (
      <ErrorBlock
        message={query.error instanceof Error ? query.error.message : String(query.error)}
      />
    );
  if (query.data.length === 0) {
    return (
      <Typography variant="meta" as="p">
        No execution servers: no capability plugin is installed, or the agent has not started its
        supervisor.
      </Typography>
    );
  }
  const ready = query.data.filter((row) => row.state === 'ready').length;
  return (
    <DataTable
      count={`${query.data.length} ${query.data.length === 1 ? 'server' : 'servers'}`}
      hint={`${ready} ready`}
      rows={query.data}
      rowKey="dist"
      columns={[
        {
          key: 'dist',
          label: 'Distribution',
          mono: true,
          strong: true,
          wrap: true,
          render: (row) => (
            <>
              {row.dist}
              {row.last_error && (
                <p
                  title={row.last_error}
                  style={{
                    marginTop: 5,
                    fontSize: 'var(--text-meta-lg)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {row.last_error}
                </p>
              )}
            </>
          ),
        },
        {
          key: 'state',
          label: 'State',
          render: (row) => <Badge tone={SERVER_STATE_COLOR[row.state]}>{row.state}</Badge>,
        },
        {
          key: 'memory',
          label: 'Memory',
          align: 'right',
          mono: true,
          render: (row) => megabytes(row.server?.rss_bytes),
        },
        {
          key: 'running',
          label: 'Running',
          align: 'right',
          mono: true,
          render: (row) =>
            row.server ? `${row.server.running} / ${row.server.max_concurrency}` : '—',
        },
        {
          key: 'queued',
          label: 'Queued',
          align: 'right',
          mono: true,
          render: (row) => row.server?.queued ?? '—',
        },
        {
          key: 'instances',
          label: 'Instances',
          align: 'right',
          mono: true,
          render: (row) => row.server?.instances ?? '—',
        },
        { key: 'restarts', label: 'Restarts', align: 'right', mono: true },
        {
          key: 'last_served',
          label: 'Last served',
          render: (row) =>
            row.server?.last_served_at ? relativeTime(row.server.last_served_at) : '—',
        },
      ]}
    />
  );
}
